/* eslint-disable react/prop-types */
import React from 'react'
import styled from 'styled-components'
import { Route, Switch } from 'react-router-dom'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap'

import {
  PATH as PROJECT_INFO_PATH,
  HEADER as PROJECT_INFO_HEADER,
} from '../project-information/constants'
import { PATH as HOME_PATH, HEADER as HOME_HEADER } from '../home/constants'
import { PPS_ALERT } from '../../constants'

import { HEADER } from './constants'
import ProjectSheet from './project-sheet'

import OverviewListTable from './containers/OverviewListTable'
import SubmitListTable from './containers/SubmitListTable'
import ImportTableContainer from './containers/ImportTableContainer'
import ImportListMenu from './containers/ImportListMenu'

import ProjectListMenu from '../../components/project-profit-sheet/ProjectListMenu'

import Tab from '../../lib/components/Tab'
import Button from '../../lib/components/Button'
import LoadingBar, { Overlay } from '../../lib/components/LoadingBar'
import Manual from '../../lib/components/Manual'
import Typography from '../../lib/typography'

import bindActionCreators from '../../lib/utils/bindActionCreators'

import { authAction, ppsAction, lobAction } from '../../actions'
import { authSelector, ppsSelector, lobSelector } from '../../selectors'

import withLayout from '../../helpers/withLayout'

import {
  project_profit_sheet_pdf as manual_pdf,
  project_profit_sheet_images as manual_images,
} from '../../static/images/manual'

const Layout = {
  Root: styled.div`
    height: 100%;
    position: relative;

    ${Overlay} {
      height: calc(100% - 50px);
      top: 50px;
    }
  `,
}

/**
 * @typedef {object} Props
 * @property {typeof authAction.auth} getAuth
 * @property {typeof lobAction.getLOB} getLOB
 * @property {typeof ppsAction.closeManual} closeManual
 * @property {typeof ppsAction.fetchProfitData} fetchProfitData
 * @property {typeof ppsAction.fetchProjectsData} fetchProjectsData
 * @property {typeof ppsAction.resetAlert} resetAlert
 * @property {boolean} isAuthorize
 * @property {boolean} isFetching
 * @property {boolean} isManualOpen
 * @property {boolean} showAccounting
 * @property {boolean} isDisplayPPSUser
 * @property {import('moment').Moment} submitMonth
 * @property {object} alertData
 */

/**
 * @typedef {import('react-router').RouteComponentProps} RouteComponentProps
 * @extends {React.Component<Props & RouteComponentProps, *>}
 */
class ProjectProfitSheetPage extends React.Component {
  state = {
    search: '',
    search_accounting: '',
    sort: {
      order: false, // true = desc
      column: 0,
    },
    type: '',
    lob: '',
    status: 'IMPLEMENTING',
  }

  componentDidMount = async () => {
    const access_token = localStorage.getItem('access_token')
    await this.props.getAuth(access_token)

    if (!this.props.isAuthorize) {
      this.props.history.replace(HOME_PATH, { header: HOME_HEADER })
    } else {
      await this.props.fetchProjectsData(access_token)
      await this.props.getLOB(access_token)
    }
  }

  handleSearch = e => {
    this.setState({ search: e })
  }

  handleClickSort = column => {
    const { sort } = this.state
    if (sort.column === column) sort.order = !sort.order
    else sort.order = true
    sort.column = column
    this.setState({ sort })
  }

  handleDropdown = (name, value) => {
    this.setState({ [name]: value })
  }

  /**
   * @param {string} to
   * @returns {import('history').LocationDescriptorObject}
   */
  urlWithHeader = to => ({
    pathname: `${this.props.match.url}${to === '/' ? '' : to}`,
    state: { header: HEADER },
  })

  goBack = () => {
    this.props.history.goBack()
    this.props.resetAlert()
  }

  toProjectInfo = () => {
    const { id } = this.props.location.state
    this.props.history.push(`${PROJECT_INFO_PATH}/${id}`, { header: PROJECT_INFO_HEADER })
    this.props.resetAlert()
  }

  refreshProfitPage = async () => {
    const access_token = localStorage.getItem('access_token')
    const { submitMonth, resetAlert, alertData, fetchProfitData } = this.props
    const { project_id } = alertData

    await resetAlert()
    await fetchProfitData(project_id, submitMonth, access_token)
  }

  toSubmitTab = async () => {
    const access_token = localStorage.getItem('access_token')
    const { fetchProjectsData, resetAlert, history, match } = this.props

    await resetAlert()
    await history.push(`${match.path}/submit`, { header: HEADER })
    await fetchProjectsData(access_token)
  }

  dispatchModalAction = name => {
    switch (name) {
      case PPS_ALERT.NOT_SUBMIT_INFO:
        return { alertCancel: this.goBack, alertConfirm: this.toProjectInfo }
      case PPS_ALERT.SUBMIT_PROFIT:
        return { alertCancel: this.refreshProfitPage, alertConfirm: this.toSubmitTab }
      default:
        return { alertCancel: () => {}, alertConfirm: () => {} }
    }
  }

  renderAccounting = () => {
    const {
      match: { path },
      showAccounting,
    } = this.props
    const { search_accounting, sort } = this.state
    return showAccounting
      ? {
        AccountingLink: (
          <Tab.MenuLink to={ this.urlWithHeader('/accounting') }>Accounting Cost</Tab.MenuLink>
        ),
        AccountingMenu: (
          <Route
            exact
            path={ `${path}/accounting` }
            render={ () => (
              <ImportListMenu
                search={ search_accounting }
                handleSearch={ e => this.setState({ search_accounting: e }) }
              />
            ) }
          />
        ),
        AccountingPage: (
          <Route
            path={ `${path}/accounting` }
            render={ () => (
              <ImportTableContainer
                search={ search_accounting }
                sort={ sort }
                handleSearch={ e => this.setState({ search_accounting: e }) }
                handleClickSort={ this.handleClickSort }
              />
            ) }
          />
        ),
      }
      : {
        AccountingLink: null,
        AccountingMenu: null,
        AccountingPage: null,
      }
  }

  render = () => {
    const {
      redirectAuth,
      resetAuth,
      lobs,
      isManualOpen,
      closeManual,
      isFetching,
      alertData,
      isDisplayPPSUser,
    } = this.props
    const { path } = this.props.match

    const { search, sort, type, status, lob, date, start_date, end_date } = this.state

    const { AccountingLink, AccountingMenu, AccountingPage } = this.renderAccounting()
    const { alertCancel, alertConfirm } = this.dispatchModalAction(alertData && alertData.name)

    const isAuthenticated = redirectAuth.isAuthenticated
    const redirectToLogin = redirectAuth.redirect

    if (redirectToLogin) {
      localStorage.clear()
      if (isAuthenticated) localStorage.setItem('bad_token', true)
      resetAuth()
      window.location = '/'
    }

    return (
      <Layout.Root>
        <LoadingBar isFetching={ isFetching } />
        <Manual pdf={ manual_pdf } images={ manual_images } open={ isManualOpen } close={ closeManual } />
        <Modal centered backdrop="static" isOpen={ !!alertData }>
          {alertData && (
            <React.Fragment>
              <ModalHeader>
                <Typography.ModalHeader>{alertData.header}</Typography.ModalHeader>
              </ModalHeader>
              <ModalBody>{alertData.body}</ModalBody>
              {(alertData.cancelText || alertData.confirmText) && (
                <ModalFooter style={ { padding: '8px 12px' } }>
                  {alertData.cancelText && (
                    <Button color="secondary" onClick={ alertCancel }>
                      {alertData.cancelText}
                    </Button>
                  )}
                  {alertData.confirmText && (
                    <Button disabled={ isDisplayPPSUser } color="success" onClick={ alertConfirm }>
                      {alertData.confirmText}
                    </Button>
                  )}
                </ModalFooter>
              )}
            </React.Fragment>
          )}
        </Modal>
        <Tab.Bar>
          <Tab.MenuLink to={ this.urlWithHeader('/overview') }>Project List</Tab.MenuLink>
          <Tab.MenuLink to={ this.urlWithHeader('/submit') }>Submit</Tab.MenuLink>
          {AccountingLink}
          <Switch>
            <Route
              exact
              path={ `${path}/overview` }
              render={ () => (
                <ProjectListMenu
                  lobs={ lobs }
                  search={ search }
                  start_date={ start_date }
                  end_date={ end_date }
                  lob={ lob }
                  type={ type }
                  status={ status }
                  handleSearch={ this.handleSearch }
                  handleDropdown={ this.handleDropdown }
                />
              ) }
            />
            <Route
              exact
              path={ `${path}/submit` }
              render={ () => (
                <ProjectListMenu
                  lobs={ lobs }
                  search={ search }
                  start_date={ start_date }
                  end_date={ end_date }
                  date={ date }
                  lob={ lob }
                  type={ type }
                  status={ status }
                  handleSearch={ this.handleSearch }
                  handleDropdown={ this.handleDropdown }
                />
              ) }
            />
            {AccountingMenu}
          </Switch>
        </Tab.Bar>
        <Switch>
          <Route
            path={ `${path}/overview` }
            render={ ({ match }) => (
              <Switch>
                <Route
                  exact
                  path={ match.path }
                  render={ props => (
                    <OverviewListTable
                      { ...props }
                      search={ search }
                      start_date={ start_date }
                      end_date={ end_date }
                      lob={ lob }
                      type={ type }
                      status={ status }
                      sort={ sort }
                      handleClickSort={ this.handleClickSort }
                    />
                  ) }
                />
                <Route path={ `${match.path}/:id` } component={ ProjectSheet } />
              </Switch>
            ) }
          />
          <Route
            path={ `${path}/submit` }
            render={ ({ match }) => (
              <Switch>
                <Route
                  exact
                  path={ match.path }
                  render={ props => (
                    <SubmitListTable
                      { ...props }
                      search={ search }
                      start_date={ start_date }
                      end_date={ end_date }
                      lob={ lob }
                      type={ type }
                      status={ status }
                      sort={ sort }
                      handleClickSort={ this.handleClickSort }
                    />
                  ) }
                />
                <Route path={ `${match.path}/:id` } component={ ProjectSheet } />
              </Switch>
            ) }
          />
          {AccountingPage}
        </Switch>
      </Layout.Root>
    )
  }
}

const mapStateToProps = createStructuredSelector({
  alertData: ppsSelector.getAlert,
  isAuthorize: ppsSelector.isAuthorize,
  isFetching: ppsSelector.isFetching,
  isManualOpen: ppsSelector.isManualOpen,
  lobs: lobSelector.getListLOB,
  redirectAuth: authSelector.redirectAuth,
  showAccounting: ppsSelector.showAccounting,
  submitMonth: ppsSelector.submitMonth,
  isDisplayPPSUser: ppsSelector.isDisplayPPSUser,
})

const mapDispatchToProps = bindActionCreators({
  closeManual: ppsAction.closeManual,
  fetchProfitData: ppsAction.fetchProfitData,
  fetchProjectsData: ppsAction.fetchProjectsData,
  getAuth: authAction.auth,
  getLOB: lobAction.getLOB,
  resetAlert: ppsAction.resetAlert,
  resetAuth: authAction.resetAuthUser,
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withLayout(ProjectProfitSheetPage))



// WEBPACK FOOTER //
// ./src/pages/project-profit-sheet/index.js